import OnboardingType from "../../../../../shared/onboarding/onboarding-type";
import { sortByName } from "../../../../../shared/utility";
import { FETCH_COSTUME_OVERVIEW, SAVE_ALIAS_COSTUME, SAVE_UNALIAS_COSTUME, DELETE_MULTIPLE_COSTUMES } from "./actions";

interface CharacterOverviewState {
  overview: any;
  loading: boolean;
  readOnly: boolean;
  onboardingSteps: OnboardingType[];
  processingCostumeIds: string[];
}

export const OnboardingStep1ClassName = 'costumes-overview-onboarding-step-1';
export const OnboardingStep2ClassName = 'costumes-overview-onboarding-step-2';
export const OnboardingStep3ClassName = 'costumes-overview-onboarding-step-3';
export const OnboardingStep4ClassName = 'costumes-overview-onboarding-step-4';

const defaultState: CharacterOverviewState = {
  overview: {},
  loading: true,
  readOnly: false,
  processingCostumeIds: [],
  onboardingSteps: [
    {
      target: '.' + OnboardingStep1ClassName,
      title: 'Costumes Acquired',
      content: `This shows the percentage of costumes acquired.`
    },
    {
      target: '.' + OnboardingStep2ClassName,
      title: 'Merge Costumes',
      content: `<p>Drag and drop costumes on top of each other to merge them.</p>
       <p>This is useful for combining duplicate costumes or aliasing costumes that are the same but have different names.</p>`
    }
  ]
};

const reducer = (state = defaultState, action: any = {}) => {
  switch (action.type) {
    case FETCH_COSTUME_OVERVIEW + "_PENDING": {
      return {
        ...state,
        loading: true,
        overview: null,
        errors: null
      };
    }

    case FETCH_COSTUME_OVERVIEW + "_FULFILLED": {
      return {
        ...state,
        loading: false,
        overview: action.payload.data,
        errors: null
      };
    }

    case FETCH_COSTUME_OVERVIEW + "_REJECTED": {
      return {
        ...state,
        loading: false,
        errors: action.payload.response?.data?.errors
      };
    }

    case SAVE_ALIAS_COSTUME + "_PENDING": {
      const aliasCostume = action.meta.aliasCostume;
      const preferredCostume = action.meta.preferredCostume;
      return {
        ...state,
        processingCostumeIds: [
          ...state.processingCostumeIds,
          aliasCostume.id,
          preferredCostume.id
        ]
      };
    }

    case SAVE_ALIAS_COSTUME + "_FULFILLED": {
      const aliasCostume = action.meta.aliasCostume;
      const preferredCostume = action.meta.preferredCostume;
      let costumes = [...state.overview.costumes];
      const costume = costumes.find((c) => c.id === preferredCostume.id);
      const aliasCostumeIndex = costumes.findIndex(
        (c) => c.id === aliasCostume.id
      );

      if (costume.aliases) {
        costume.aliases = [
          ...costume.aliases,
          aliasCostume
        ];
      } else {
        costume.aliases = [aliasCostume];
      }

      costumes[aliasCostumeIndex].aliases = [];

      costumes.splice(aliasCostumeIndex, 1);
      let overview = { ...state.overview, costumes };
      return {
        ...state,
        overview,
        processingCostumeIds: state.processingCostumeIds.filter(
          id => id !== aliasCostume.id && id !== preferredCostume.id
        )
      };
    }

    case SAVE_ALIAS_COSTUME + "_REJECTED": {
      const aliasCostume = action.meta.aliasCostume;
      const preferredCostume = action.meta.preferredCostume;
      return {
        ...state,
        errors: action.payload.response?.data?.errors,
        processingCostumeIds: state.processingCostumeIds.filter(
          id => id !== aliasCostume.id && id !== preferredCostume.id
        )
      };
    }

    case SAVE_UNALIAS_COSTUME + "_PENDING": {
      const aliasCostume = action.meta.aliasCostume;
      const preferredCostume = action.meta.preferredCostume;
      return {
        ...state,
        processingCostumeIds: [
          ...state.processingCostumeIds,
          aliasCostume.id,
          preferredCostume.id
        ]
      };
    }

    case SAVE_UNALIAS_COSTUME + "_FULFILLED": {
      const aliasCostume = action.meta.aliasCostume;
      const preferredCostume = action.meta.preferredCostume;
      let costumes = [...state.overview.costumes];
      const costume = costumes.find((c) => c.id === preferredCostume.id);

      const aliasCostumeIndex = costume.aliases.findIndex(
        (a) => a.id === aliasCostume.id
      );
      costume.aliases.splice(aliasCostumeIndex, 1);

      aliasCostume.scriptDays = action.payload.data.scriptDays;
      costumes.push(aliasCostume);

      sortByName(costumes);

      let overview = { ...state.overview, costumes };
      return {
        ...state,
        overview,
        processingCostumeIds: state.processingCostumeIds.filter(
          id => id !== aliasCostume.id && id !== preferredCostume.id
        )
      };
    }

    case SAVE_UNALIAS_COSTUME + "_REJECTED": {
      const aliasCostume = action.meta.aliasCostume;
      const preferredCostume = action.meta.preferredCostume;
      return {
        ...state,
        processingCostumeIds: state.processingCostumeIds.filter(
          id => id !== aliasCostume.id && id !== preferredCostume.id
        )
      };
    }

    case DELETE_MULTIPLE_COSTUMES + "_PENDING": {
      return {
        ...state,
        loading: true,
        errors: null
      };
    }

    case DELETE_MULTIPLE_COSTUMES + "_FULFILLED": {
      const deletedCostumeIds = action.meta.costumeIds;
      let costumes = state.overview.costumes.filter(
        (costume) => !deletedCostumeIds.includes(costume.id)
      );

      let overview = { ...state.overview, costumes };
      return {
        ...state,
        overview: overview,
        loading: false
      };
    }

    case DELETE_MULTIPLE_COSTUMES + "_REJECTED": {
      return {
        ...state,
        errors: action.payload.response.data.errors,
        loading: false
      };
    }

    default:
      return state;
  }
};

export default reducer;
